import { useLocale } from '@hooks/useLocale';
import { useTranslation } from 'next-i18next';
import { memo, useCallback, useMemo, useState } from 'react';
import useObjectVisibility from '@hooks/useObjectVisibility';

import PdpDealsBannerList from '@components/product/PdpDealsBannerList/index';
import getDealsBannerStickerTypes from '@components/product/getDealsBannerStickerTypes';
import getPdpDealsBannerData from '@adapters/prismic/fetching/pdpDealsBanner/getPdpDealsBannerData';
import {
  applyCodeDrivenSlot1Cap,
  getResolvedDealsBanners,
} from '@components/product/ProductPriceAndReviewsContainer/getResolvedDealsBanners';
import GiftCardForm from '@components/product/ProductBuyZone/GiftCardForm';
import ProductAddToCartButtonWrapper from '@components/product/ProductBuyZone/ProductAddToCartButtonWrapper';
import ProductFullfillment from '_components/Product/ProductFulfillment';
import PdpColourPicker from '_components/ColourPicker/PdpColourPickerV2';
import QuantitySelector from '_components/Product/QuantitySelector';
import SizeSelection from '_components/Product/SizeSelection';
import StickyAddToCartBar from '_components/Product/StickyAddToCartBar';
import PromotionMessaging from '@components/product/ProductPromotionMessaging';
import Callout from '_components/UI/Callout';
import ProductGuarantee from '_components/Product/PurchaseGuarantee';
import {
  FEATURE_FLAG_DEALS_BANNER,
  FEATURE_FLAG_DEALS_BANNER_SALE,
  FEATURE_FLAG_NEW_YEARS_PROMOTION,
  FEATURE_FLAG_OVERRIDE_PDP_VALUE_PROPS,
  FEATURE_FLAG_NEW_SPU_PROMOTION,
  FEATURE_FLAG_PDP_2026_1,
  FEATURE_FLAG_PDP_SIZE_MERCHANDISING,
  FEATURE_FLAG_STICKY_ATC_BAR,
} from '@config/feature-flag';
import {
  MecProduct,
  MecVariant,
  PriceType,
} from '@adapters/bigcommerce/types/product';
import valuePropsEn from 'prismic-value-props-EN.json';
import valuePropsFr from 'prismic-value-props-FR.json';
import { hasReturnRestriction } from '@utils/pdp/defaultPDPValueProps';
import InStorePromotion from '_components/Product/InStorePromoMessage';
import ProductStrip from '_components/ProductStrip';

import {
  FitSizeValueInterface,
  SizeChartInterface,
} from '@adapters/bigcommerce/types/sizeChart';
import ProductInformationSection from '_components/Product/ProductInformationSection';
import FindInStoreSection from '_components/Product/FindInStore/FindInStoreSection';

import { usePdp } from '@context/PdpContext';
import SoldAndShippedByTag from '_components/Product/SoldAndShippedByTag';
import {
  overrideValuePropsEn,
  overrideValuePropsFr,
} from '@utils/pdp/defaultPDPValueProps';
import { MEC_SELLER_NAME } from '@utils/constants';
import { resolvedBannerToTrackingLabel } from '@utils/gtm/ga4/utils';
import { useMerchandisingSizeSelection } from '@hooks/useMerchandisingSizeSelection';
import {
  PRODUCT_MINI_SINGLE,
  PRODUCT_MINI_CAROUSEL,
} from '@components/global/Recommendations/Loader';
import { AlgonomyPlacement } from '@components/global/Recommendations';
import MiniProductCard from '_components/MiniProductStrip/MiniProductCard';

import styles from './ProductBuyZone.module.scss';

interface ProductBuyZoneProps {
  product: MecProduct;
  productRelativePath: string;
  measureData: SizeChartInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  fitSentimentData: any;
  hasRestrictions: string;
  restrictions: MecProduct['shippingRestrictions'];
  productCode: string;
  abVariant: string | null;
  albyRef: React.RefObject<HTMLDivElement | null>;
  scrollToProductReviewsAndAnswers?: () => void;
  recommendations?: AlgonomyPlacement[];
  outfittingAnchor?: React.ReactNode;
}

interface MarketplaceInfo {
  shouldDisplayMarketplace: boolean;
  isMarketplace: boolean;
  marketplaceSellerName: string;
}

const getMarketplaceInfo = (
  selectedVariant: MecVariant | null | undefined,
  variants: MecVariant[] | null | undefined
): MarketplaceInfo => {
  // By default, we don't show the marketplace messaging... unless they've picked a variant already, or all the possible
  // variants are MEC, or all the variants are a marketplace seller

  const availableVariants =
    variants?.filter(
      (variant) => variant.hasStockAvailable && variant.availableForSaleOnline
    ) ?? [];
  const marketplaceVariants = availableVariants.filter(
    (variant) => variant.isMarketplace
  );

  // in this case, they have selected a variant now, so we can key the marketplace stuff off of that
  if (selectedVariant) {
    return {
      shouldDisplayMarketplace: true,
      isMarketplace: selectedVariant.isMarketplace,
      marketplaceSellerName:
        selectedVariant.marketplaceSellerName ?? MEC_SELLER_NAME,
    };
  }

  // If no available variants, don't display marketplace info
  if (availableVariants.length === 0) {
    return {
      shouldDisplayMarketplace: false,
      isMarketplace: false,
      marketplaceSellerName: MEC_SELLER_NAME,
    };
  }

  // in this case, all variants are marketplace products
  // Assume they're all from the same seller. Take the first one's seller name.
  if (marketplaceVariants.length === availableVariants.length) {
    return {
      shouldDisplayMarketplace: true,
      isMarketplace: true,
      marketplaceSellerName:
        availableVariants[0].marketplaceSellerName ?? MEC_SELLER_NAME,
    };
  }

  // If there are marketplace variants, but no selected variant, don't display marketplace info
  if (marketplaceVariants.length > 0 && !selectedVariant) {
    return {
      shouldDisplayMarketplace: false,
      isMarketplace: true,
      marketplaceSellerName:
        marketplaceVariants[0].marketplaceSellerName ?? 'MEC',
    };
  }

  // If there are marketplace variants, but no selected variant, don't display marketplace info
  if (marketplaceVariants.length > 0 && !selectedVariant) {
    return {
      shouldDisplayMarketplace: false,
      isMarketplace: true,
      marketplaceSellerName:
        marketplaceVariants[0].marketplaceSellerName ?? 'MEC',
    };
  }

  // No variants are marketplace products OR mixed marketplace/MEC variants
  return {
    shouldDisplayMarketplace: true,
    isMarketplace: false,
    marketplaceSellerName: MEC_SELLER_NAME,
  };
};

const ProductBuyZone = ({
  product,
  productRelativePath,
  measureData,
  fitSentimentData,
  fitAndRise,
  hasRestrictions,
  restrictions,
  scrollToProductReviewsAndAnswers,
  recommendations = [],
  outfittingAnchor,
}: ProductBuyZoneProps) => {
  const locale = useLocale();
  const { t } = useTranslation('common');
  const {
    brand,
    categoryTargets,
    price,
    actualPriceList,
    hasMemberPriceDifference,
    promoBadge,
    selectedAvailability,
    selectedColour,
    selectedVariant,
    variants,
  } = usePdp();

  // A plain useRef never re-triggers useObjectVisibility's effect once its .current
  // populates, so if this anchor isn't rendered on mount (it's gated on
  // selectedAvailability) and only mounts later, no observer ever gets attached. A
  // callback ref backed by state gives the hook a genuinely new ref object exactly when
  // the anchor (dis)appears, so it re-observes correctly either way.
  const [ctaAnchorNode, setCtaAnchorNode] = useState<HTMLDivElement | null>(
    null
  );
  const ctaAnchorRef = useCallback(
    (node: HTMLDivElement | null) => setCtaAnchorNode(node),
    []
  );
  const ctaAnchorRefObject = useMemo(
    () => ({ current: ctaAnchorNode }),
    [ctaAnchorNode]
  );
  const isMainCtaVisible = useObjectVisibility(ctaAnchorRefObject, 0, false);

  const merchandisingSelection = useMerchandisingSizeSelection(
    FEATURE_FLAG_PDP_SIZE_MERCHANDISING
      ? product.sizeSelectionMerchandising
      : undefined
  );

  const prismicValueProps = locale === 'en' ? valuePropsEn : valuePropsFr;
  const overrideValueProps =
    locale === 'en' ? overrideValuePropsEn : overrideValuePropsFr;

  const valueProps = FEATURE_FLAG_OVERRIDE_PDP_VALUE_PROPS
    ? overrideValueProps
    : prismicValueProps;

  const { shouldDisplayMarketplace, marketplaceSellerName } =
    getMarketplaceInfo(selectedVariant, variants);

  const pdpDealsBannerEntries = useMemo(() => {
    if (!FEATURE_FLAG_DEALS_BANNER) {
      return [];
    }

    return getPdpDealsBannerData(locale ?? 'en');
  }, [locale]);

  const allResolvedDealsBanners = useMemo(() => {
    if (!FEATURE_FLAG_DEALS_BANNER) {
      return [];
    }

    return getResolvedDealsBanners(pdpDealsBannerEntries, {
      brand: brand?.name,
      categoryTargets,
      stickerTypes: getDealsBannerStickerTypes(promoBadge),
      style: selectedColour?.name,
      variantSku: selectedVariant?.sku ?? null,
      activePriceGroup: actualPriceList?.groupKey ?? null,
    });
  }, [
    actualPriceList?.groupKey,
    brand?.name,
    categoryTargets,
    pdpDealsBannerEntries,
    promoBadge,
    selectedColour?.name,
    selectedVariant?.sku,
  ]);

  const resolvedSlot2DealsBanners = useMemo(
    () => allResolvedDealsBanners.filter((banner) => banner.position === 2),
    [allResolvedDealsBanners]
  );

  const prismicDealsBannerTitles = useMemo(() => {
    const codeDrivenSlot1Count =
      (FEATURE_FLAG_PDP_2026_1 && price?.priceType === PriceType.Clearance
        ? 1
        : 0) +
      (hasMemberPriceDifference && price?.priceType !== PriceType.Clearance
        ? 1
        : 0) +
      (FEATURE_FLAG_DEALS_BANNER_SALE && !!price?.epochEndDate ? 1 : 0);
    const cappedSlot1 = applyCodeDrivenSlot1Cap(
      allResolvedDealsBanners.filter((b) => b.position === 1),
      codeDrivenSlot1Count
    );
    const slot2 = allResolvedDealsBanners.filter((b) => b.position === 2);
    return [...cappedSlot1, ...slot2]
      .map(resolvedBannerToTrackingLabel)
      .filter((label): label is string => label !== null);
  }, [
    allResolvedDealsBanners,
    price?.priceType,
    price?.epochEndDate,
    hasMemberPriceDifference,
  ]);

  const miniProductSingleRecommendation = useMemo(
    () => recommendations?.find((item) => item.name === PRODUCT_MINI_SINGLE),
    [recommendations]
  );

  const miniProductSingleItem = miniProductSingleRecommendation?.items?.[0];

  const miniProductRecommendations = recommendations.filter(
    (item) => item.name === PRODUCT_MINI_CAROUSEL
  );

  return (
    <>
      <div data-pdp-scroll-main-info>
        {!product?.isGiftCard && !selectedAvailability && (
          <div className={styles.restrictionsContainer}>
            {hasRestrictions && (
              <Callout
                classNames={styles.itemAlert}
                key={restrictions.updated_at}
              >
                <p
                  data-testid="top-buyzone-alert-message"
                  dangerouslySetInnerHTML={{
                    __html:
                      locale !== 'fr'
                        ? restrictions.message_en
                        : restrictions.message_fr,
                  }}
                />
              </Callout>
            )}
          </div>
        )}
        <div className={styles.colourSelector}>
          <PdpColourPicker
            path={productRelativePath}
            product={product}
            prismicDealsBannerTitles={prismicDealsBannerTitles}
          />
        </div>

        <div className={styles.sizeSelection}>
          <SizeSelection
            sizeDetail={product?.sizeData}
            measure={measureData}
            fitAndRise={fitAndRise}
            isBike={product?.isBike}
            merchandisingSelection={merchandisingSelection}
          />
        </div>

        {product?.isGiftCard ? (
          <GiftCardForm />
        ) : (
          <div className={styles.quantitySelector}>
            <QuantitySelector />
          </div>
        )}

        {FEATURE_FLAG_DEALS_BANNER && resolvedSlot2DealsBanners.length > 0 && (
          <div className={styles.dealsBannerContainer}>
            <PdpDealsBannerList
              banners={resolvedSlot2DealsBanners}
              containerClassName={styles.dealsBanner}
            />
          </div>
        )}

        {FEATURE_FLAG_NEW_SPU_PROMOTION && (
          <div className={styles.dealsBannerContainer}>
            <InStorePromotion />
          </div>
        )}

        {!product.isGiftCard && <ProductFullfillment />}
        {shouldDisplayMarketplace && (
          <SoldAndShippedByTag sellerName={marketplaceSellerName} />
        )}

        {selectedAvailability && (
          <div
            id="add-to-cart"
            data-pdp-scroll-cta-anchor
            className={styles.addToCartAnchor}
            ref={ctaAnchorRef}
          >
            <ProductAddToCartButtonWrapper
              prismicDealsBannerTitles={prismicDealsBannerTitles}
              renderPersistentBar={!FEATURE_FLAG_STICKY_ATC_BAR}
            />
          </div>
        )}

        {FEATURE_FLAG_NEW_YEARS_PROMOTION && (
          <PromotionMessaging
            startDate="2026-01-01 00:00"
            endDate="2026-01-01 23:59"
          />
        )}
      </div>
      <div data-pdp-scroll-secondary-info>
        {hasRestrictions &&
          selectedAvailability &&
          restrictions.restriction_level !== 'R4' && (
            <div className={styles.restrictionsContainer}>
              <Callout
                classNames={styles.itemAlert}
                key={restrictions.updated_at}
              >
                <p
                  data-testid="bottom-buyzone-alert-message"
                  dangerouslySetInnerHTML={{
                    __html:
                      locale !== 'fr'
                        ? restrictions.message_en
                        : restrictions.message_fr,
                  }}
                />
              </Callout>
            </div>
          )}

        <div className={styles.productGuarantee}>
          <ProductGuarantee
            hasReturnRestriction={hasReturnRestriction(product)}
          />
        </div>
        <FindInStoreSection />

        {miniProductRecommendations?.length > 0 && (
          <div className={styles.miniProductStrip}>
            <ProductStrip placement={miniProductRecommendations[0]} mini />
          </div>
        )}

        {miniProductSingleItem && (
          <div className={styles.miniProductSingle}>
            <MiniProductCard
              title={miniProductSingleRecommendation?.message}
              displayName={miniProductSingleItem.displayName}
              displayPrice={miniProductSingleItem.displayPrice}
              imageURL={miniProductSingleItem.imageURL}
              href={
                miniProductSingleItem.linkURL ?? miniProductSingleItem.detailURL
              }
              displaySalePrice={miniProductSingleItem.displaySalePrice}
            />
          </div>
        )}

        {outfittingAnchor}

        <ProductInformationSection
          product={product}
          fitSentimentData={fitSentimentData}
          measureData={measureData}
          fitAndRise={fitAndRise}
          isBike={product?.isBike}
          scrollToProductReviewsAndAnswers={scrollToProductReviewsAndAnswers}
          isGiftCard={product?.isGiftCard}
        />

        {product.quebecRepairDisclosureFlag && (
          <div
            className={styles.qcTeaser}
            dangerouslySetInnerHTML={{ __html: t('qc.bill29.teaser') }}
          />
        )}
      </div>
      {FEATURE_FLAG_STICKY_ATC_BAR && selectedAvailability && (
        <StickyAddToCartBar
          isVisible={!isMainCtaVisible}
          prismicDealsBannerTitles={prismicDealsBannerTitles}
        />
      )}
    </>
  );
};

export default memo(ProductBuyZone);
